// components/templates/sections/SpecialMenuSection.tsx
'use client';
import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useDesign } from '@/context/DesignContext';

interface MenuItem {
  name: string;
  description: string;
  price: string;
}

interface MenuCategory {
  name: string;
  items: MenuItem[];
  leftImage?: string;
  rightImage?: string;
}

interface SpecialMenuSectionProps {
  subtitle: string;
  title: string;
  description: string;
  categories: MenuCategory[];
  viewAllLink?: string;
  design?: any;
}

export default function SpecialMenuSection({
  subtitle,
  title,
  description,
  categories=[],
  viewAllLink,
  design: propDesign,
}: SpecialMenuSectionProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;
  const [activeIndex, setActiveIndex] = useState(0);
  const activeCat = categories[activeIndex];

  if (!activeCat) {
  return <div className="text-center py-10">Aucun élément de menu pour le moment.</div>;
}
  const headingFont = design?.typography?.headingFont || "'Sora', sans-serif";
  const bodyFont = design?.typography?.bodyFont || "'Plus Jakarta Sans', sans-serif";
  const primaryColor = design?.colors?.primary || '#E07A5F';
  const textColor = design?.colors?.text || '#1E1E1E';
  const borderColor = design?.colors?.border || '#E9ECEF';
  const btnPrimary = design?.buttons?.primary || {
    class: 'tf-button style1',
    backgroundColor: 'var(--primary)',
    textColor: '#FFFFFF',
    borderRadius: '30px',
    padding: '12px 30px',
  };

  const tabInactiveStyle: React.CSSProperties = {
    borderBottom: `2px solid ${borderColor}`,
    color: textColor,
    cursor: 'pointer',
    padding: '10px 20px',
    fontFamily: headingFont,
  };
  const tabActiveStyle: React.CSSProperties = {
    borderBottom: `2px solid ${primaryColor}`,
    color: primaryColor,
    cursor: 'pointer',
    padding: '10px 20px',
    fontFamily: headingFont,
  };
  const itemNameStyle: React.CSSProperties = {
    fontFamily: headingFont,
    fontSize: '1.2rem',
  };
  const priceStyle: React.CSSProperties = {
    color: primaryColor,
    fontWeight: 'bold',
  };
  const viewAllStyle: React.CSSProperties = {
    backgroundColor: btnPrimary.backgroundColor,
    color: btnPrimary.textColor,
    padding: btnPrimary.padding,
    borderRadius: btnPrimary.borderRadius,
    display: 'inline-block',
    textDecoration: 'none',
    marginTop: '2rem',
  };

  return (
    <section className="s-menu">
      <div className="shape" data-aos="fade-right">
        <Image src="/assets/images/section/shape-menu.png" alt="" width={200} height={200} />
      </div>
      <div className="container" style={{ maxWidth: design?.spacing?.containerMaxWidth || '1200px', margin: '0 auto' }}>
        <div className="row">
          <div className="menu-content">
            <div className="block-text center style-2">
              <p className="subtitle" style={{ fontFamily: headingFont, color: primaryColor }} data-aos="fade-up">{subtitle}</p>
              <h3 className="title" style={{ fontFamily: headingFont, color: textColor }} data-aos="fade-up">{title}</h3>
              <p className="text" style={{ fontFamily: bodyFont }} data-aos="fade-up">{description}</p>
            </div>
            <div className="flat-tabs" data-aos="fade-up">
              <ul className="menu-tab" style={{ display: 'flex', listStyle: 'none', borderBottom: `1px solid ${borderColor}` }}>
                {categories.map((cat, idx) => (
                  <li
                    key={idx}
                    className={idx === activeIndex ? 'active' : ''}
                    onClick={() => setActiveIndex(idx)}
                    style={idx === activeIndex ? tabActiveStyle : tabInactiveStyle}
                  >
                    <h5>{cat.name}</h5>
                  </li>
                ))}
              </ul>
              <div className="content-tab">
                <div className="content-inner">
                  <div className="container_inner">
                    {activeCat.leftImage && (
                      <div className="left-img">
                        <Image src={activeCat.leftImage} alt="" width={300} height={400} />
                      </div>
                    )}
                    {activeCat.rightImage && (
                      <div className="right-img">
                        <Image src={activeCat.rightImage} alt="" width={300} height={400} />
                      </div>
                    )}
                    <ul className="menu-list" style={{ listStyle: 'none', padding: 0 }}>
                      {activeCat.items.map((item, idx) => (
                        <li key={idx} style={{ marginBottom: '1rem' }}>
                          <h5 className="name" style={itemNameStyle}>
                            <span className="txt">{item.name}</span>
                            <span className="price" style={priceStyle}>{item.price}</span>
                          </h5>
                          <p style={{ fontFamily: bodyFont }}>{item.description}</p>
                        </li>
                      ))}
                      <Link href={viewAllLink} className={btnPrimary.class} style={viewAllStyle}>view all menu</Link>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
